import { useEffect, useState } from "react";
import {
  Target,
  ShieldCheck,
  Users,
  Sparkles,
  Compass,
  HeartHandshake,
} from "lucide-react";
import { Container, SectionHeading, Eyebrow } from "@/components/common/Section";
import Reveal from "@/components/common/Reveal";
import CtaBand from "@/components/common/CtaBand";
import { fetchContent } from "@/lib/apiClient";

const FALLBACK = {
  about_mission:
    "To replace fragmented, manual, and legacy processes with secure, unified software — so agencies and enterprises can focus on outcomes instead of paperwork.",
  about_story:
    "Sentira is the evolution of our founding work on the Enforcement Hub project. As our vision grew beyond a single tool, we rebranded to reflect who we are today: a product and development company — led by our flagship platform, Licara.",
};

const values = [
  { icon: ShieldCheck, title: "Trust by design", desc: "Security, transparency, and reliability are non-negotiable in everything we ship." },
  { icon: Compass, title: "Mission-driven", desc: "We build for the people who keep communities running — agencies, officers, and staff." },
  { icon: Sparkles, title: "Craft & clarity", desc: "Complex operations deserve software that feels simple, fast, and considered." },
  { icon: HeartHandshake, title: "Partnership", desc: "We succeed when our customers do — from onboarding through every renewal." },
];

const stats = [
  { value: "2025", label: "Rebranded as Sentira" },
  { value: "Licara", label: "Our flagship platform" },
  { value: "Public + Private", label: "Sectors we serve" },
];

export default function About() {
  const [c, setC] = useState(FALLBACK);

  useEffect(() => {
    fetchContent()
      .then((d) => setC({ ...FALLBACK, ...d }))
      .catch(() => setC(FALLBACK));
  }, []);

  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden">
        <div className="hero-glow pointer-events-none absolute inset-0" />
        <div className="bg-sentira-grid bg-radial-fade pointer-events-none absolute inset-0 opacity-60" />
        <Container className="relative py-20 md:py-24">
          <Reveal>
            <Eyebrow>About Sentira</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.08] tracking-[-0.02em] sm:text-5xl">
              We build the software that keeps public operations moving
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Sentira is a technology solutions and development company. We
              design and ship mission-critical systems for government agencies,
              municipalities, and enterprises — combining enterprise-grade
              engineering with a deep respect for the people who use our tools.
            </p>
          </Reveal>
        </Container>
      </section>

      <section className="py-8 md:py-12">
        <Container>
          <div className="grid gap-6 sm:grid-cols-3">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.06}>
                <div className="rounded-[var(--radius-card)] border border-border bg-card p-6">
                  <p className="text-2xl font-semibold md:text-3xl">{s.value}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-24">
        <Container>
          <div className="grid gap-12 lg:grid-cols-2">
            <Reveal>
              <div>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-secondary">
                  <Target className="h-5 w-5 text-primary" />
                </div>
                <h2 className="mt-5 text-2xl font-semibold tracking-[-0.01em]">Our mission</h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  {c.about_mission}
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-secondary">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <h2 className="mt-5 text-2xl font-semibold tracking-[-0.01em]">The rebrand story</h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  {c.about_story}
                </p>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-24">
        <Container>
          <SectionHeading eyebrow="What we value" title="Principles that guide our work" />
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="h-full rounded-[var(--radius-card)] border border-border bg-card p-6">
                  <v.icon className="h-6 w-6 text-primary" />
                  <h3 className="mt-4 text-lg font-semibold">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-24">
        <Container>
          <div className="rounded-[24px] border border-border bg-[hsl(220_30%_8%)] p-8 text-center md:p-12">
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-secondary">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <h2 className="mx-auto mt-5 max-w-xl text-2xl font-semibold tracking-[-0.01em] sm:text-3xl">
              A small, senior team building for the long term
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
              We're engineers, designers, and implementation specialists who
              care deeply about the mission. We're growing — and always looking
              for people who want to build software that matters.
            </p>
          </div>
        </Container>
      </section>

      <CtaBand title="Let's build something that matters" description="Whether you're evaluating Licara or exploring custom development, we'd love to talk." primaryLabel="Request a demo" secondaryLabel="Contact us" />
    </div>
  );
}
